import React, { useState, useEffect, useRef } from 'react';
import { CreditCard as Edit, User, Upload, X, Loader as Loader2, Check, Facebook, Twitter, Instagram, Linkedin, Youtube, Github, TriangleAlert as AlertTriangle } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { supabase } from '../../../lib/supabase';
import { uploadToR2, deleteFromR2 } from '../../../lib/r2-storage';
import { toast } from '../../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../ui/alert-dialog';

interface ProfileSettingsProps {
  isGlobalDarkMode: boolean;
}

export const ProfileSettings: React.FC<ProfileSettingsProps> = ({ isGlobalDarkMode: _ }) => {
  const { profile, refreshProfile, user } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    bio: '',
    facebook_url: '',
    twitter_url: '',
    instagram_url: '',
    linkedin_url: '',
    youtube_url: '',
    github_url: '',
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        bio: profile.bio || '',
        facebook_url: profile.facebook_url || '',
        twitter_url: profile.twitter_url || '',
        instagram_url: profile.instagram_url || '',
        linkedin_url: profile.linkedin_url || '',
        youtube_url: profile.youtube_url || '',
        github_url: profile.github_url || '',
      });
    }
  }, [profile]);

  const handleSave = async () => {
    if (!profile) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          bio: formData.bio,
          facebook_url: formData.facebook_url,
          twitter_url: formData.twitter_url,
          instagram_url: formData.instagram_url,
          linkedin_url: formData.linkedin_url,
          youtube_url: formData.youtube_url,
          github_url: formData.github_url,
        })
        .eq('id', profile.id);

      if (error) throw error;

      await refreshProfile();
      setIsEditing(false);
      toast({
        title: 'Profile updated',
        description: 'Your profile has been updated successfully.',
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to update profile. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        bio: profile.bio || '',
        facebook_url: profile.facebook_url || '',
        twitter_url: profile.twitter_url || '',
        instagram_url: profile.instagram_url || '',
        linkedin_url: profile.linkedin_url || '',
        youtube_url: profile.youtube_url || '',
        github_url: profile.github_url || '',
      });
    }
    setIsEditing(false);
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !profile) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JPG, PNG, GIF, or WEBP image.',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 5MB.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploadingAvatar(true);
    try {
      if (profile.avatar_url && profile.avatar_url.includes('agencyplus.io')) {
        await deleteFromR2(profile.avatar_url);
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;

      const { publicUrl } = await uploadToR2({
        fileName,
        file,
        folder: `avatars/${profile.id}`
      });

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      await refreshProfile();
      toast({
        title: 'Avatar updated',
        description: 'Your profile image has been updated successfully.',
      });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload avatar. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    if (!profile || !profile.avatar_url) return;

    setIsUploadingAvatar(true);
    try {
      if (profile.avatar_url && profile.avatar_url.includes('agencyplus.io')) {
        await deleteFromR2(profile.avatar_url);
      }

      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', profile.id);

      if (error) throw error;

      await refreshProfile();
      toast({
        title: 'Avatar removed',
        description: 'Your profile image has been removed.',
      });
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove avatar. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || !profile) return;

    setIsDeleting(true);
    try {
      if (profile.avatar_url && profile.avatar_url.includes('agencyplus.io')) {
        await deleteFromR2(profile.avatar_url);
      }

      const { error: workspaceMembersError } = await supabase
        .from('workspace_members')
        .delete()
        .eq('user_id', user.id);

      if (workspaceMembersError) throw workspaceMembersError;

      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', profile.id);

      if (profileError) throw profileError;

      const { error: deleteError } = await supabase.auth.admin.deleteUser(user.id);

      if (deleteError) {
        await supabase.auth.signOut();
      }

      toast({
        title: 'Account deleted',
        description: 'Your account has been permanently deleted.',
      });

      navigate('/login');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete account. Please try again or contact support.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-workspace-accent" />
      </div>
    );
  }

  const inputClass = 'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-workspace-accent disabled:opacity-60 bg-background border-input text-foreground placeholder:text-muted-foreground';

  return (
    <div className="space-y-6">
      {/* Profile Image Upload Card */}
      <div className="rounded-lg border p-6 bg-card border-border">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-workspace-accent/15 text-workspace-accent">
            <User className="w-5 h-5" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Profile Image</h3>
        </div>

        <div className="flex items-center gap-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full flex items-center justify-center border-2 overflow-hidden border-border bg-muted">
              {profile.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-12 h-12 text-muted-foreground" />
              )}
            </div>
            {profile.avatar_url && !isUploadingAvatar && (
              <button
                onClick={handleRemoveAvatar}
                className="absolute -top-2 -right-2 p-1 rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            )}
            {isUploadingAvatar && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
          </div>

          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">Upload New Image</label>
                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleAvatarUpload}
                    disabled={isUploadingAvatar}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingAvatar}
                    className="px-4 py-2 bg-workspace-accent hover:bg-workspace-accent-hover text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Upload className="w-4 h-4" />
                    Choose File
                  </button>
                  <span className="text-xs text-muted-foreground">or drag and drop</span>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-muted">
                <h4 className="text-sm font-medium mb-2 text-foreground">File Requirements</h4>
                <p className="text-xs text-muted-foreground">
                  <strong>Supported formats:</strong> JPG, PNG, GIF<br />
                  <strong>Maximum size:</strong> 5MB<br />
                  <strong>Recommended dimensions:</strong> 400x400 pixels
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information Card */}
      <div className="rounded-lg border p-6 bg-card border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Personal information</h3>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="p-2 rounded-lg transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <Edit className="w-4 h-4" />
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-3 py-1.5 bg-workspace-accent hover:bg-workspace-accent-hover text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">First Name</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                disabled={!isEditing}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Last Name</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                disabled={!isEditing}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Email</label>
              <input
                type="email"
                value={profile.email}
                disabled
                className={`${inputClass} cursor-not-allowed`}
              />
              <p className="text-xs mt-1 text-muted-foreground">Email cannot be changed</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                disabled={!isEditing}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-foreground">Bio</label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              disabled={!isEditing}
              rows={4}
              placeholder="Tell us about yourself..."
              className={`${inputClass} resize-none`}
            />
          </div>

          <div>
            <h4 className="text-sm font-medium mb-3 text-foreground">Social Media Links</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium mb-2 text-muted-foreground">
                  <Facebook className="w-3 h-3 inline mr-1" />
                  Facebook
                </label>
                <input
                  type="url"
                  value={formData.facebook_url}
                  onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                  disabled={!isEditing}
                  placeholder="https://facebook.com/username"
                  className={`${inputClass} text-sm`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-2 text-muted-foreground">
                  <Twitter className="w-3 h-3 inline mr-1" />
                  X (Twitter)
                </label>
                <input
                  type="url"
                  value={formData.twitter_url}
                  onChange={(e) => setFormData({ ...formData, twitter_url: e.target.value })}
                  disabled={!isEditing}
                  placeholder="https://x.com/username"
                  className={`${inputClass} text-sm`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-2 text-muted-foreground">
                  <Instagram className="w-3 h-3 inline mr-1" />
                  Instagram
                </label>
                <input
                  type="url"
                  value={formData.instagram_url}
                  onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                  disabled={!isEditing}
                  placeholder="https://instagram.com/username"
                  className={`${inputClass} text-sm`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-2 text-muted-foreground">
                  <Linkedin className="w-3 h-3 inline mr-1" />
                  LinkedIn
                </label>
                <input
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  disabled={!isEditing}
                  placeholder="https://linkedin.com/in/username"
                  className={`${inputClass} text-sm`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-2 text-muted-foreground">
                  <Youtube className="w-3 h-3 inline mr-1" />
                  YouTube
                </label>
                <input
                  type="url"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  disabled={!isEditing}
                  placeholder="https://youtube.com/@username"
                  className={`${inputClass} text-sm`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-2 text-muted-foreground">
                  <Github className="w-3 h-3 inline mr-1" />
                  GitHub
                </label>
                <input
                  type="url"
                  value={formData.github_url}
                  onChange={(e) => setFormData({ ...formData, github_url: e.target.value })}
                  disabled={!isEditing}
                  placeholder="https://github.com/username"
                  className={`${inputClass} text-sm`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Card */}
      <div className={`rounded-lg border p-6 bg-card ${profile.scheduled_for_deletion_at ? 'border-yellow-500/50 bg-yellow-500/5' : 'border-destructive/30'}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 rounded-lg ${profile.scheduled_for_deletion_at ? 'bg-yellow-500/15 text-yellow-500' : 'bg-destructive/15 text-destructive'}`}>
            <AlertTriangle className="w-5 h-5" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">
            {profile.scheduled_for_deletion_at ? 'Account Deletion Scheduled' : 'Delete Account'}
          </h3>
        </div>

        {profile.scheduled_for_deletion_at ? (
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              Your account is scheduled for permanent deletion on{' '}
              <strong className="text-yellow-500">
                {new Date(profile.scheduled_for_deletion_at).toLocaleDateString()} at {new Date(profile.scheduled_for_deletion_at).toLocaleTimeString()}
              </strong>.
            </p>
            <p className="text-sm text-muted-foreground">
              You can still use your account until then. If you change your mind, you can cancel the deletion below.
            </p>
            <button
              onClick={async () => {
                try {
                  const { error } = await supabase
                    .from('profiles')
                    .update({ scheduled_for_deletion_at: null })
                    .eq('id', profile.id);
                  if (error) throw error;
                  await refreshProfile();
                  toast({
                    title: 'Deletion cancelled',
                    description: 'Your account is no longer scheduled for deletion.',
                  });
                } catch (err) {
                  console.error('Error cancelling deletion:', err);
                  toast({
                    title: 'Error',
                    description: 'Failed to cancel deletion. Please try again.',
                    variant: 'destructive',
                  });
                }
              }}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium transition-colors"
            >
              Cancel Deletion
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>

            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30">
              <h4 className="text-sm font-semibold mb-2 text-destructive">This will permanently delete:</h4>
              <ul className="text-sm space-y-1 text-destructive/80">
                <li>• Your profile and personal information</li>
                <li>• All workspace memberships</li>
                <li>• Your uploaded files and avatar</li>
                <li>• All associated data and settings</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    disabled={isDeleting}
                    className="flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 bg-destructive hover:bg-destructive/90 text-white disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                    Delete Now
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-card border-border">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-foreground">Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription className="text-muted-foreground">
                      This action cannot be undone. Your account and all data will be permanently removed immediately.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-secondary text-secondary-foreground border-border">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAccount}
                      className="bg-destructive hover:bg-destructive/90 text-white font-semibold"
                    >
                      Delete Permanently
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <button
                onClick={async () => {
                  const sevenDaysFromNow = new Date();
                  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
                  
                  try {
                    const { error } = await supabase
                      .from('profiles')
                      .update({ scheduled_for_deletion_at: sevenDaysFromNow.toISOString() })
                      .eq('id', profile.id);
                      
                    if (error) throw error;
                    await refreshProfile();
                    toast({
                      title: 'Account scheduled for deletion',
                      description: 'Your account will be deleted in 7 days.',
                    });
                  } catch (err) {
                    console.error('Error scheduling deletion:', err);
                    toast({
                      title: 'Error',
                      description: 'Failed to schedule deletion. Please try again.',
                      variant: 'destructive',
                    });
                  }
                }}
                className="flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/80 text-foreground border border-border"
              >
                Schedule Deletion (7 Days)
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
